import { literal, unsafeStatic } from 'lit/static-html.js';
import type { LitElement } from 'lit';
import type {
	Constructor,
	HTMLTag,
	PolymorphicTagInterface,
} from './polymorphic-tag.types';

/**
 * This mixin allows a component to render with different types of tags, based on the provided `allowedTags`.
 * Be aware that, this mixin does **NOT** change the type (TS-wise) of your component, so, for tags that have their specific
 * attributes (e.g., button, a), your component must accept and properly render those attributes.
 * This is just a convenience layer for rendering a dynamically provided tag.
 *
 * TL;DR; either limit the tags you will allow for component, or add the additional attributes to your component to
 * comply with the tags that will be supported.
 */
export const PolymorphicTagMixin = <T extends Constructor<LitElement>>(
	superClass: T,
	allowedTags: HTMLTag[] = ['article', 'div', 'span'],
	fallbackTag: HTMLTag = 'div',
) => {
	class PolymorphicTag extends superClass {
		static get properties() {
			return {
				as: { type: String },
			};
		}

		/**
		 * Which HTML tag should be used to render this element.
		 */
		declare as: HTMLTag;

		override connectedCallback() {
			super.connectedCallback();

			this.as =
				(allowedTags.includes(this.as) ? this.as : fallbackTag) ?? 'div';
		}

		get tag() {
			return literal`${unsafeStatic(this.as)}`;
		}
	}

	return PolymorphicTag as Constructor<PolymorphicTagInterface> & T;
};
